import { useState, useEffect, useMemo, useRef } from 'react';
import { getScans, getTeamMembers, exportScansCsv, getCleaningCompanies, createLateCheckout, getProperties } from '../lib/api';
import { getUser } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { RefreshCw, Clock, Calendar as CalendarIcon, CalendarDays, Users, X, CheckCircle2, Play, Download, LogOut, SprayCan, Timer, CalendarClock } from 'lucide-react';
import MobileNav from '../components/MobileNav';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar } from '../components/ui/calendar';
import StaffFilterSelect from '../components/StaffFilterSelect';
import LateCheckoutModal from '../components/LateCheckoutModal';
import PullToRefresh from '../components/PullToRefresh';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

export default function LogsPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // null = all dates
  const [showCalendar, setShowCalendar] = useState(false);
  const [staffFilter, setStaffFilter] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [cleaningGroups, setCleaningGroups] = useState([]);
  const calendarRef = useRef(null);
  const exportRef = useRef(null);
  const [showExport, setShowExport] = useState(false);
  const [exportRange, setExportRange] = useState(undefined); // { from, to }
  const [exporting, setExporting] = useState(false);
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateProperties, setLateProperties] = useState([]);
  const user = getUser();
  const isAdmin = user?.role === 'admin';

  const formatDateForAPI = (date) => date.toISOString().split('T')[0];

  const handleExport = async () => {
    if (!exportRange?.from) {
      toast.error('Pick a start date for the report');
      return;
    }
    setExporting(true);
    try {
      const start = formatDateForAPI(exportRange.from);
      const end = formatDateForAPI(exportRange.to || exportRange.from);
      const res = await exportScansCsv(start, end, isAdmin && staffFilter !== 'all' ? staffFilter : null);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `turnover_${start}_${end}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
      setShowExport(false);
    } catch (e) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const loadScans = async (silent = false) => {
    try {
      const response = await getScans(
        200,
        selectedDate ? formatDateForAPI(selectedDate) : null,
        isAdmin && staffFilter !== 'all' ? staffFilter : null
      );
      setScans(response.data.scans || []);
    } catch (error) {
      if (!silent) toast.error('Failed to load scan logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadScans();
  }, [selectedDate, staffFilter]);

  useAutoRefresh(() => loadScans(true));

  useEffect(() => {
    if (isAdmin) {
      getTeamMembers()
        .then((res) => setStaffList((res.data.members || []).filter((m) => m.role === 'staff')))
        .catch(() => {});
      getProperties()
        .then((res) => setLateProperties(res.data.properties || res.data || []))
        .catch(() => {});
      getCleaningCompanies()
        .then((res) => {
          const groups = (res.data.companies || [])
            .map((c) => ({ id: c.id, name: c.name, cleaners: c.cleaners || [] }))
            .filter((g) => g.cleaners.length > 0);
          setCleaningGroups(groups);
        })
        .catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setShowExport(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadScans();
  };

  const getStatusBadge = (status) => {
    if (status === 'accepted') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-xs font-semibold whitespace-nowrap">
          <LogOut size={11} /> Checked out
        </span>
      );
    }
    const statusConfig = {
      rate_limited: { label: 'Rate Limited', class: 'status-rate-limited' },
      invalid_token: { label: 'Invalid', class: 'status-invalid' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-600' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const getDateKey = (timestamp) => new Date(timestamp).toISOString().split('T')[0];

  const formatDateHeader = (dateKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const targetDate = new Date(dateKey + 'T00:00:00');
    if (targetDate.getTime() === today.getTime()) return 'Today';
    if (targetDate.getTime() === yesterday.getTime()) return 'Yesterday';
    const day = String(targetDate.getDate()).padStart(2, '0');
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${targetDate.getFullYear()}`;
  };

  const formatTime = (timestamp, tz) => {
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz || undefined });
    } catch (e) {
      return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
  };

  const groupedScans = useMemo(() => {
    const groups = {};
    scans.forEach((scan) => {
      const dateKey = getDateKey(scan.timestamp);
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(scan);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    return sortedKeys.map((dateKey) => ({ dateKey, label: formatDateHeader(dateKey), scans: groups[dateKey] }));
  }, [scans]);

  const parseTs = (v) => { try { return v ? new Date(v) : null; } catch (e) { return null; } };
  const fmtDur = (mins) => {
    if (mins == null) return null;
    const m = Math.round(mins);
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h && r) return `${h}h ${r}m`;
    if (h) return `${h}h`;
    return `${r}m`;
  };

  // Timeline badges + cleaning/turnover metrics for an accepted checkout
  const ScanDetails = ({ scan }) => {
    if (scan.status !== 'accepted') return null;
    const checkedOut = scan.checked_out_at || scan.timestamp;
    const start = scan.cleaning_started_at;
    const ready = scan.ready_at;
    const coDt = parseTs(checkedOut), stDt = parseTs(start), rDt = parseTs(ready);
    const cleaningMins = rDt && stDt ? (rDt - stDt) / 60000 : null;
    const turnoverMins = rDt && coDt ? (rDt - coDt) / 60000 : null;
    return (
      <div className="mt-2.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-xs font-semibold whitespace-nowrap" data-testid={`scan-checkedout-${scan.id}`}>
            <LogOut size={11} /> Checked out {formatTime(checkedOut, scan.timezone)}
          </span>
          {start && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-semibold whitespace-nowrap" data-testid={`scan-cleaning-${scan.id}`}>
              <SprayCan size={11} /> {formatTime(start, scan.timezone)}{scan.cleaning_started_by ? ` · ${scan.cleaning_started_by}` : ''}
            </span>
          )}
          {ready && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-semibold whitespace-nowrap" data-testid={`scan-ready-${scan.id}`}>
              <CheckCircle2 size={11} /> {formatTime(ready, scan.timezone)}{scan.ready_by ? ` · ${scan.ready_by}` : ''}
            </span>
          )}
          {!start && !ready && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold whitespace-nowrap">
              <Play size={11} /> Awaiting cleaning
            </span>
          )}
        </div>
        {(cleaningMins != null || turnoverMins != null) && (
          <div className="flex items-center gap-4 mt-2.5 pt-2.5 border-t border-slate-100">
            {cleaningMins != null && (
              <div className="inline-flex items-center gap-1.5 text-xs" title="Time spent cleaning (start → ready)">
                <span className="w-6 h-6 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><SprayCan size={12} className="text-amber-500" /></span>
                <span className="text-slate-400 font-medium">Cleaning</span>
                <span className="font-bold text-slate-700">{fmtDur(cleaningMins)}</span>
              </div>
            )}
            {turnoverMins != null && (
              <div className="inline-flex items-center gap-1.5 text-xs" title="Total turnover (checkout → ready)">
                <span className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><Timer size={12} className="text-blue-500" /></span>
                <span className="text-slate-400 font-medium">Turnover</span>
                <span className="font-bold text-slate-700">{fmtDur(turnoverMins)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const selectedStaffName = (() => {
    if (staffFilter === 'all') return null;
    if (staffFilter === 'team:all') return 'My team';
    if (staffFilter === 'cc:all') return 'All cleaning companies';
    if (staffFilter.startsWith('cc:')) {
      const g = cleaningGroups.find((x) => x.id === staffFilter.slice(3));
      return g ? g.name : 'Cleaning company';
    }
    const inTeam = staffList.find((s) => s.id === staffFilter);
    return inTeam ? (inTeam.name || inTeam.email) : 'Staff';
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={() => loadScans()}>
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <MobileNav />

      <main className="flex-1 mobile-container pb-28 md:pb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-2">Scan Activity</h1>
            <p className="text-slate-600 font-medium">Monitor all checkout activity</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <div className="relative" ref={exportRef}>
                <Button
                  onClick={() => setShowExport((v) => !v)}
                  variant="outline"
                  className="h-12 rounded-xl gap-2 font-semibold"
                  data-testid="export-csv-button"
                >
                  <Download size={18} />
                  <span className="hidden sm:inline">Export</span>
                </Button>
                <AnimatePresence>
                  {showExport && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.97 }}
                      className="absolute top-full right-0 mt-2 z-50 w-[320px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-200 p-4"
                      data-testid="export-popover"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Download size={16} className="text-emerald-500" />
                        <h3 className="font-bold text-slate-900">Turnover report</h3>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">
                        Pick a date range to export a CSV of checkouts and cleaning times{selectedStaffName ? ` for ${selectedStaffName}` : ''}.
                      </p>
                      <div className="flex justify-center">
                        <Calendar
                          mode="range"
                          selected={exportRange}
                          onSelect={setExportRange}
                          disabled={(date) => date > new Date()}
                          numberOfMonths={1}
                          className="rounded-xl"
                        />
                      </div>
                      <div className="mt-2 text-xs text-slate-500 text-center min-h-[16px]">
                        {exportRange?.from
                          ? `${exportRange.from.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} → ${(exportRange.to || exportRange.from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                          : 'Select a start and end date'}
                      </div>
                      <Button
                        onClick={handleExport}
                        disabled={exporting || !exportRange?.from}
                        className="w-full h-11 rounded-xl mt-3 bg-gradient-to-r from-blue-500 to-emerald-500 text-white font-bold"
                        data-testid="export-download-button"
                      >
                        {exporting ? 'Preparing...' : 'Download CSV'}
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline" className="h-12 rounded-xl" data-testid="refresh-button">
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {/* Schedule late checkout (admin only) */}
        {isAdmin && (
          <button
            onClick={() => setShowLateModal(true)}
            data-testid="schedule-late-checkout-button"
            className="w-full mb-3 h-12 rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold flex items-center justify-center gap-2 shadow-sm transition-all active:scale-[0.99]"
          >
            <CalendarClock size={18} /> Schedule Late Checkout
          </button>
        )}

        {/* Filters */}
        <div className="flex flex-row gap-3 mb-6">
          {/* Date filter */}
          <div className="relative flex-1" ref={calendarRef}>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              data-testid="date-filter-button"
              className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-slate-200 text-sm font-semibold text-slate-700 shadow-sm flex items-center justify-between cursor-pointer hover:border-slate-300 transition-colors"
            >
              <CalendarDays size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-500" />
              <span>{selectedDate ? selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'All dates'}</span>
              {selectedDate && (
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); setSelectedDate(null); setShowCalendar(false); }}
                  className="w-6 h-6 rounded-lg hover:bg-slate-100 flex items-center justify-center"
                  data-testid="clear-date-button"
                >
                  <X size={15} className="text-slate-400" />
                </span>
              )}
            </button>
            <AnimatePresence>
              {showCalendar && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  className="absolute top-full left-0 right-0 mt-2 z-50 flex justify-center"
                >
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-2 max-w-[calc(100vw-2rem)]">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => { if (date) { setSelectedDate(date); setShowCalendar(false); } }}
                      disabled={(date) => date > new Date()}
                      className="rounded-xl"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Staff filter - Admin only */}
          {isAdmin && (staffList.length > 0 || cleaningGroups.length > 0) && (
            <div className="relative flex-1">
              <StaffFilterSelect
                value={staffFilter}
                onChange={setStaffFilter}
                staffList={staffList}
                cleaningGroups={cleaningGroups}
              />
            </div>
          )}
        </div>

        {selectedStaffName && (
          <p className="text-sm text-slate-500 mb-4 -mt-2">Showing checkouts for <span className="font-semibold text-slate-700">{selectedStaffName}</span>'s properties</p>
        )}

        {scans.length === 0 ? (
          <div className="app-card p-8 text-center" data-testid="no-scans">
            <Clock size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No checkouts found</h3>
            <p className="text-slate-500">
              {selectedDate || staffFilter !== 'all' ? 'Try adjusting the filters above' : 'Scans will appear here when guests check out'}
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="grouped-scans">
            {groupedScans.map((group, groupIndex) => (
              <motion.div
                key={group.dateKey}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.1 }}
                data-testid={`date-group-${group.dateKey}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-xl shadow-md">
                    <CalendarIcon size={16} className="text-white" />
                    <span className="text-white font-semibold text-sm">{group.label}</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-blue-200 to-transparent"></div>
                </div>

                <div className="space-y-3 pl-2">
                  {group.scans.map((scan, scanIndex) => (
                    <motion.div
                      key={scan.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: groupIndex * 0.1 + scanIndex * 0.03 }}
                      className="card-3d p-4 hover:shadow-lg transition-shadow"
                      data-testid={`scan-log-${scan.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="min-w-0">
                          <h3 className="text-base font-bold text-slate-900 truncate">{scan.propertyName}</h3>
                          {scan.propertyAddress && (
                            <p className="text-xs text-slate-500 truncate">{scan.propertyAddress}</p>
                          )}
                        </div>
                        {scan.status !== 'accepted' && getStatusBadge(scan.status)}
                      </div>
                      {scan.status === 'accepted' ? (
                        <ScanDetails scan={scan} />
                      ) : (
                        <div className="inline-flex items-center gap-1.5 mt-1 text-xs text-slate-500">
                          <Clock size={12} /> {formatTime(scan.timestamp, scan.timezone)}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {scans.length > 0 && (
          <div className="mt-6 text-center text-sm text-slate-500">
            Showing {scans.length} checkout{scans.length !== 1 ? 's' : ''}
          </div>
        )}

        <LateCheckoutModal
          open={showLateModal}
          onClose={() => setShowLateModal(false)}
          properties={lateProperties}
          onCreate={createLateCheckout}
        />
      </main>
    </div>
    </PullToRefresh>
  );
}
